import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import "../roles/Roles.css";
import { getBrandingItems, createBranding, updateBranding, deleteBranding } from "../../../utils/api";

const TZ = "America/Paramaribo";
const toDateInput = (value) =>
  value ? new Date(value).toLocaleDateString("en-CA", { timeZone: TZ }) : "";
const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("nl-NL", { timeZone: TZ, day: "numeric", month: "short", year: "numeric" }) : "-";

const statusOf = (item) => {
  if (!item.isActive) return { key: "off", label: "Uitgeschakeld" };
  const now = Date.now();
  if (new Date(item.startDate).getTime() > now) return { key: "planned", label: "Gepland" };
  if (new Date(item.endDate).getTime() < now) return { key: "ended", label: "Afgelopen" };
  return { key: "live", label: "Nu zichtbaar" };
};

const columns = [
  {
    key: "logoUrl",
    label: "Logo",
    render: (value) => (value ? <img src={value} alt="" className="branding-preview" /> : "-"),
  },
  { key: "name", label: "Naam" },
  { key: "startDate", label: "Van", render: formatDate },
  { key: "endDate", label: "Tot en met", render: formatDate },
  {
    key: "isActive",
    label: "Status",
    render: (_v, item) => {
      const s = statusOf(item);
      return <span className={`branding-status ${s.key}`}>{s.label}</span>;
    },
  },
];

const emptyForm = { name: "", startDate: "", endDate: "", isActive: true, file: null, preview: "" };

export default function Branding() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [listKey, setListKey] = useState(0);

  const openAdd = () => {
    setSelected(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setSelected(item);
    setForm({
      name: item.name || "",
      startDate: toDateInput(item.startDate),
      endDate: toDateInput(item.endDate),
      isActive: item.isActive !== false,
      file: null,
      preview: item.logoUrl || "",
    });
    setModalOpen(true);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((p) => ({ ...p, file, preview: URL.createObjectURL(file) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected && !form.file) {
      alert("Kies een logo-afbeelding.");
      return;
    }
    if (form.endDate < form.startDate) {
      alert("De einddatum ligt voor de startdatum.");
      return;
    }
    const data = new FormData();
    data.append("name", form.name);
    data.append("startDate", form.startDate);
    data.append("endDate", form.endDate);
    data.append("isActive", String(form.isActive));
    if (form.file) data.append("logoFile", form.file);

    setSaving(true);
    try {
      if (selected) await updateBranding(selected._id, data);
      else await createBranding(data);
      setModalOpen(false);
      setListKey((k) => k + 1);
    } catch (err) {
      alert(err.message || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <p className="roles-intro">
          Het standaardlogo blijft altijd in de app en het beheerpaneel staan. Hier plant u een <strong>tijdelijk logo</strong> voor
          een periode, bijvoorbeeld voor Diwali of Holi. Tijdens die periode tonen de app en het beheerpaneel automatisch dit logo;
          daarna komt het standaardlogo vanzelf terug. Overlappen twee periodes, dan wint het logo met de laatste startdatum.
          Het icoon op het startscherm van de telefoon verandert niet.
        </p>
        <AdminList
          key={listKey}
          title="Logo's"
          fetchData={getBrandingItems}
          deleteData={deleteBranding}
          columns={columns}
          onEdit={openEdit}
          onAdd={openAdd}
          searchPlaceholder="Zoek op naam..."
        />
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={selected ? "Tijdelijk logo bewerken" : "Tijdelijk logo plannen"}
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Naam *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Bijv. Diwali 2026"
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Van *</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Tot en met *</label>
              <input
                type="date"
                value={form.endDate}
                min={form.startDate || undefined}
                onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label>Logo-afbeelding {selected ? "(laat leeg om te behouden)" : "*"}</label>
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFile} />
            <small style={{ color: "#666" }}>Bij voorkeur een vierkante PNG met transparante achtergrond, minimaal 512×512 px.</small>
            {form.preview && (
              <div style={{ marginTop: 10 }}>
                <img src={form.preview} alt="Voorbeeld" className="branding-preview" style={{ width: 120, height: 120 }} />
              </div>
            )}
          </div>
          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                style={{ width: "auto" }}
              />
              Ingeschakeld
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Annuleren</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Opslaan..." : "Opslaan"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
